import React, { useState, useEffect, useCallback } from 'react'
import { Table } from 'react-bootstrap'
import { FaSearch } from 'react-icons/fa'
import API from '../../../service/api'
import { customToast } from '../../../utils/toasts'
import './ApiUsers.css'

const emptyForm = {
  name: '',
  apiType: 'PUSH',
  clientCode: '',
  address: '-',
  mobileNumber: '-',
  email: '-',
  password: '-',
  apiDataTransmitFreqInSec: 120,
  oauthEnabled: false,
  oauthUrl: '',
  oauthClientId: '',
  oauthClientSecret: '',
  oauthVendorName: ''
}

const ApiUsers = () => {
  const [users, setUsers] = useState([])
  const [searchKey, setSearchKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [editModal, setEditModal] = useState(null)
  const [form, setForm] = useState({ ...emptyForm })

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      const res = await API({ url: '/api-users', method: 'GET', manageLoader: false })
      setUsers(res?.data?.data || [])
    } catch (err) {
      console.error('Failed to fetch API users', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  const filtered = users.filter(u => {
    if (!searchKey) return true
    const s = searchKey.toLowerCase()
    return (u.name || '').toLowerCase().includes(s) ||
      (u.clientCode || '').toLowerCase().includes(s) ||
      (u.email || '').toLowerCase().includes(s)
  })

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  // -- Add --
  const openAddModal = () => {
    setForm({ ...emptyForm })
    setAddModal(true)
  }

  const handleCreate = async () => {
    if (!form.name || !form.clientCode) {
      customToast({ message: 'Name and Client Code are required', type: 'error' })
      return
    }
    try {
      const res = await API({ url: '/api-users', method: 'POST', data: form, manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: 'API User created successfully' })
        setAddModal(false)
        fetchUsers()
      } else {
        customToast({ message: res?.data?.message || 'Create failed', type: 'error' })
      }
    } catch (err) {
      console.error('Create API user failed', err)
    }
  }

  // -- Edit --
  const openEditModal = (user) => {
    setForm({
      name: user.name || '',
      apiType: user.apiType || 'PUSH',
      clientCode: user.clientCode || '',
      address: user.address || '-',
      mobileNumber: user.mobileNumber || '-',
      email: user.email || '-',
      password: user.password || '-',
      apiDataTransmitFreqInSec: user.apiDataTransmitFreqInSec || 120,
      oauthEnabled: user.oauthEnabled || false,
      oauthUrl: user.oauthUrl || '',
      oauthClientId: user.oauthClientId || '',
      oauthClientSecret: user.oauthClientSecret || '',
      oauthVendorName: user.oauthVendorName || ''
    })
    setEditModal(user)
  }

  const handleUpdate = async () => {
    if (!form.name || !form.clientCode) {
      customToast({ message: 'Name and Client Code are required', type: 'error' })
      return
    }
    try {
      const res = await API({ url: `/api-users/${editModal._id}`, method: 'PUT', data: form, manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: 'API User updated successfully' })
        setEditModal(null)
        fetchUsers()
      }
    } catch (err) {
      console.error('Update API user failed', err)
    }
  }

  // -- Toggle Status --
  const handleToggle = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate'
    if (!window.confirm(`Are you sure you want to ${action} this API user?`)) return
    try {
      const res = await API({ url: `/api-users/${userId}/toggle-status`, method: 'PUT', manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: `API User ${action}d successfully` })
        fetchUsers()
      }
    } catch (err) {
      console.error('Toggle status failed', err)
    }
  }

  // -- Delete --
  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this API user?')) return
    try {
      const res = await API({ url: `/api-users/${userId}`, method: 'DELETE', manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: 'API User deleted successfully' })
        fetchUsers()
      }
    } catch (err) {
      console.error('Delete API user failed', err)
    }
  }

  const activeCount = filtered.filter(u => u.isActive).length
  const inactiveCount = filtered.filter(u => !u.isActive).length

  const renderFormModal = (title, onSave, onCancel) => (
    <div className='au-modal-overlay' onClick={onCancel}>
      <div className='au-modal' onClick={e => e.stopPropagation()}>
        <div className='au-modal-header'>
          <h6>{title}</h6>
          <button className='au-modal-close' onClick={onCancel}>&times;</button>
        </div>
        <div className='au-modal-body'>
          <div className='au-form-row'>
            <div className='au-form-group'>
              <label>Name *</label>
              <input value={form.name} onChange={e => handleFormChange('name', e.target.value)} placeholder='e.g. Delhivery' />
            </div>
            <div className='au-form-group'>
              <label>Client Code *</label>
              <input value={form.clientCode} onChange={e => handleFormChange('clientCode', e.target.value.toUpperCase())} placeholder='e.g. DELH0001' />
            </div>
          </div>
          <div className='au-form-row'>
            <div className='au-form-group'>
              <label>API Type</label>
              <select value={form.apiType} onChange={e => handleFormChange('apiType', e.target.value)}>
                <option value='PUSH'>PUSH</option>
                <option value='PULL'>PULL</option>
              </select>
            </div>
            <div className='au-form-group'>
              <label>Transmit Frequency (sec)</label>
              <input type='number' value={form.apiDataTransmitFreqInSec} onChange={e => handleFormChange('apiDataTransmitFreqInSec', Number(e.target.value))} />
            </div>
          </div>
          <div className='au-form-row'>
            <div className='au-form-group'>
              <label>Email</label>
              <input value={form.email} onChange={e => handleFormChange('email', e.target.value)} />
            </div>
            <div className='au-form-group'>
              <label>Mobile Number</label>
              <input value={form.mobileNumber} onChange={e => handleFormChange('mobileNumber', e.target.value)} />
            </div>
          </div>
          <div className='au-form-group'>
            <label>Address</label>
            <input value={form.address} onChange={e => handleFormChange('address', e.target.value)} />
          </div>
          <div className='au-form-group'>
            <label>Password</label>
            <input value={form.password} onChange={e => handleFormChange('password', e.target.value)} />
          </div>
          <div style={{ borderTop: '1px solid #eee', marginTop: 16, paddingTop: 12 }}>
            <div className='au-form-row'>
              <div className='au-form-group'>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type='checkbox' checked={form.oauthEnabled} onChange={e => handleFormChange('oauthEnabled', e.target.checked)} style={{ width: 'auto' }} />
                  OAuth Enabled
                </label>
              </div>
              <div className='au-form-group'>
                <label>Vendor Name</label>
                <input value={form.oauthVendorName} onChange={e => handleFormChange('oauthVendorName', e.target.value)} placeholder='e.g. magenta' disabled={!form.oauthEnabled} />
              </div>
            </div>
            {form.oauthEnabled && (
              <>
                <div className='au-form-group'>
                  <label>OAuth URL</label>
                  <input value={form.oauthUrl} onChange={e => handleFormChange('oauthUrl', e.target.value)} placeholder='https://auth.example.com/token' />
                </div>
                <div className='au-form-row'>
                  <div className='au-form-group'>
                    <label>OAuth Client ID</label>
                    <input value={form.oauthClientId} onChange={e => handleFormChange('oauthClientId', e.target.value)} />
                  </div>
                  <div className='au-form-group'>
                    <label>OAuth Client Secret</label>
                    <input value={form.oauthClientSecret} onChange={e => handleFormChange('oauthClientSecret', e.target.value)} type='password' />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className='au-modal-footer'>
          <button className='au-btn-cancel' onClick={onCancel}>Cancel</button>
          <button className='au-btn-save' onClick={onSave}>Save</button>
        </div>
      </div>
    </div>
  )

  return (
    <div className='au-container'>
      <div className='au-header'>
        <h5 className='au-title'>API Users</h5>
        <div className='au-stats'>
          <span className='au-badge au-badge-active'>{activeCount} Active</span>
          <span className='au-badge au-badge-inactive'>{inactiveCount} Inactive</span>
          <span className='au-badge au-badge-total'>{filtered.length} Total</span>
        </div>
      </div>

      <div className='au-toolbar'>
        <div className='au-search-box'>
          <FaSearch className='au-search-icon' />
          <input
            type='text'
            placeholder='Search by name, client code, or email...'
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className='au-search-input'
          />
        </div>
        <div className='au-toolbar-actions'>
          <button className='au-add-btn' onClick={openAddModal}>+ Add API User</button>
          <button className='au-refresh-btn' onClick={fetchUsers} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className='au-table-wrapper'>
        <Table bordered hover size='sm' className='au-table'>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Client Code</th>
              <th>API Type</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Frequency (sec)</th>
              <th>Hit Count</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 && (
              <tr><td colSpan={10} className='text-center'>Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={10} className='text-center'>No API users found</td></tr>
            )}
            {filtered.map((u, i) => (
              <tr key={u._id}>
                <td>{i + 1}</td>
                <td><strong>{u.name}</strong></td>
                <td>{u.clientCode}</td>
                <td><span className='au-type-badge'>{u.apiType}</span></td>
                <td>{u.email}</td>
                <td>{u.mobileNumber}</td>
                <td>{u.apiDataTransmitFreqInSec}s</td>
                <td>{u.apiHitCount || 0}</td>
                <td>
                  <span className={`au-status ${u.isActive ? 'au-status-active' : 'au-status-inactive'}`}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button className='au-action-btn au-btn-edit' onClick={() => openEditModal(u)}>Edit</button>
                  <button
                    className={`au-action-btn ${u.isActive ? 'au-btn-danger' : 'au-btn-add'}`}
                    onClick={() => handleToggle(u._id, u.isActive)}
                  >
                    {u.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button className='au-action-btn au-btn-danger' onClick={() => handleDelete(u._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Add Modal */}
      {addModal && renderFormModal('Add New API User', handleCreate, () => setAddModal(false))}

      {/* Edit Modal */}
      {editModal && renderFormModal(`Edit API User - ${editModal.clientCode}`, handleUpdate, () => setEditModal(null))}
    </div>
  )
}

export default ApiUsers
